import { useMemo } from 'react';

import type { MissionDependency, MissionEdgeType } from '../types';

const MISSION_COMMAND_TYPES: Record<string, MissionEdgeType> = {
  'RealWorld.Commands.RevealMission': 'reveal',
  'RealWorld.Commands.UnlockMission': 'unlock',
  'RealWorld.Commands.AccomplishMission': 'accomplish',
  'RealWorld.Commands.LockMission': 'lock'
};

function parseMissionDependencies(missions: Mission[]): MissionDependency[] {
  const dependencies = new Map<string, MissionDependency>();
  const missionIds = new Set(missions.map((mission) => mission.id));

  for (const mission of missions) {
    if (mission.quests) {
      for (const quest of mission.quests) {
        const reactions = quest.actionHandler?.reactionDefinitions;
        if (reactions) {
          for (const reaction of reactions) {
            for (const command of reaction.commands ?? []) {
              const edgeType = MISSION_COMMAND_TYPES[command.type];
              if (edgeType) {
                const rawTargetMissionId = command.parameters?.missionId as
                  | number
                  | string
                  | undefined;
                const targetMissionId = Number(rawTargetMissionId);
                const isValidTarget =
                  Number.isFinite(targetMissionId) &&
                  missionIds.has(targetMissionId) &&
                  targetMissionId !== mission.id;

                if (isValidTarget) {
                  const key = `${mission.id}-${targetMissionId}-${edgeType}`;
                  dependencies.set(key, {
                    sourceMissionId: mission.id,
                    targetMissionId,
                    edgeType
                  });
                }
              }
            }
          }
        }
      }
    }
  }

  return [...dependencies.values()];
}

export default function useMissionDependencies(
  missions: Mission[] | undefined
): MissionDependency[] {
  return useMemo(() => {
    if (!missions) return [];
    return parseMissionDependencies(missions);
  }, [missions]);
}
